<template>
	<div class="container mx-auto max-w-5xl px-4">
		<div v-if="!inHeader" class="flex overflow-hidden p-2">
			<HelpfulTips>
				Your {{ form.model }} may come in different trim levels. Please choose the trim
				level that matches your ride as well as its set of options.
			</HelpfulTips>

			<div class="grow overflow-y-auto lg:basis-1/2" :class="{'pb-12': !dialogs.headerFormModalOpen && !dialogs.headerFormModalError}">
				<div class="text-20 text-main-400 mb-4 block text-center font-bold">
					{{ inHeader ? 'Find your ride' : `${form.year}   ${form.make}  ${form.model}` }}
				</div>

				<div
					v-if="!inHeader"
					v-for="trim in form.trims"
					:key="trim.trade_pending_id"
					class="hover:border-main-300 hover:bg-main-300 group mx-auto mb-4 box-border flex w-auto cursor-pointer flex-col items-start rounded-xl border border-gray-300 px-4 py-3 ease-in-out lg:py-4"
					:class="{
						'border-main-400 bg-main-400 text-white':
							form.trim.trade_pending_id === trim.trade_pending_id &&
							form.trim.trim_name === trim.trim_name,
					}"
					@click="onSelectTrim(trim)"
				>
					<p
						class="text-md text-main-400 mb-3 text-left font-bold ease-in-out group-hover:text-white lg:mb-2 lg:text-xl"
						:class="{
							'!text-white':
								form.trim.trade_pending_id === trim.trade_pending_id &&
								form.trim.trim_name === trim.trim_name,
						}"
					>
						{{ trim.body_type }} {{ trim.trim_name }} {{ trim.drivetrain_key }}
					</p>
					<div class="flex w-full justify-between">
						<span
							class="text-11 lg:text:base text-secondary group-hover:text-white"
							:class="{
								'!text-white':
									form.trim.trade_pending_id === trim.trade_pending_id &&
									form.trim.trim_name === trim.trim_name,
							}"
						>
							<span class="text-11 font-bold lg:text-base">Vehicle Type:&nbsp;</span>
							<span class="text-11 lg:text-base">{{ trim.body_type }},&nbsp;</span>
							<br class="lg:hidden" />
							<span class="text-11 font-bold lg:text-base">Engine:&nbsp;</span>
							<span class="text-11 lg:text-base">{{ trim.engine }},&nbsp;</span>
							<br class="lg:hidden" />
							<span class="text-11 font-bold lg:text-base">Drivetrain:&nbsp;</span>
							<span class="text-11 lg:text-base">{{ trim.drivetrain }},&nbsp;</span>
							<template v-if="trim.licensePlate && trim.licensePlate.transmission">
								<br class="lg:hidden" />
								<span class="text-11 font-bold lg:text-base">
									Transmission:&nbsp;
								</span>
								<span class="text-11 lg:text-base">
									{{ trim.licensePlate.transmission }},
								</span>
							</template>
							<br class="lg:hidden" />
							<span class="text-11 font-bold lg:text-base">Doors:&nbsp;</span>
							<span class="text-11 lg:text-base">{{ trim.doors }}</span>
							<template v-if="trim.licensePlate && trim.licensePlate.color">
								<span>,&nbsp;</span>
								<br class="lg:hidden" />
								<span class="text-11 font-bold lg:text-base">Color:&nbsp;</span>
								<span class="text-11 lg:text-base">
									{{ trim.licensePlate.color.name }}
								</span>
							</template>
						</span>

						<div class="ml-2">
							<AppCheckbox
								class="text-success-base mt-1 border-0"
								:checked="
									form.trim.trade_pending_id === trim.trade_pending_id &&
									form.trim.trim_name === trim.trim_name
								"
							/>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="relative" v-else>
			<div class="text-20 text-main-400 mb-4 block text-center font-bold">
				{{ inHeader ? 'Find your ride' : `${form.year}   ${form.make}  ${form.model}` }}
			</div>

			<div
				class="absolute -right-3 -top-3 z-[3] h-6 w-6 cursor-pointer hidden"
				@click="dialogs.closeTrimsModal()"
			>
				<CloseSvg class="fill-gray-400" />
			</div>

			<div class="max-h-[300px] overflow-y-auto pr-2">
				<div
					v-for="trim in form.trimsModal"
					:key="trim.trade_pending_id"
					class="hover:border-main-300 hover:bg-main-300 group mx-auto mb-4 box-border flex w-auto cursor-pointer flex-col items-start rounded-xl border border-gray-300 px-4 py-3 ease-in-out lg:py-4"
					:class="{
						'border-main-400 bg-main-400 text-white':
							form.trimModal.trade_pending_id === trim.trade_pending_id &&
							form.trimModal.trim_name === trim.trim_name,
					}"
					@click="onSelectTrimModal(trim)"
				>
					<p
						class="text-md text-main-400 mb-3 text-left font-bold ease-in-out group-hover:text-white lg:mb-2 lg:text-xl"
						:class="{
							'!text-white':
								form.trimModal.trade_pending_id === trim.trade_pending_id &&
								form.trimModal.trim_name === trim.trim_name,
						}"
					>
						{{ trim.body_type }} {{ trim.trim_name }} {{ trim.drivetrain_key }}
					</p>
					<div class="flex w-full justify-between">
						<span
							class="text-11 lg:text:base text-secondary group-hover:text-white"
							:class="{
								'!text-white':
									form.trimModal.trade_pending_id === trim.trade_pending_id &&
									form.trimModal.trim_name === trim.trim_name,
							}"
						>
							<span class="text-11 font-bold lg:text-base">Vehicle Type:&nbsp;</span>
							<span class="text-11 lg:text-base">{{ trim.body_type }},&nbsp;</span>
							<br class="lg:hidden" />
							<span class="text-11 font-bold lg:text-base">Engine:&nbsp;</span>
							<span class="text-11 lg:text-base">{{ trim.engine }},&nbsp;</span>
							<br class="lg:hidden" />
							<span class="text-11 font-bold lg:text-base">Drivetrain:&nbsp;</span>
							<span class="text-11 lg:text-base">{{ trim.drivetrain }},&nbsp;</span>
							<template v-if="trim.licensePlate && trim.licensePlate.transmission">
								<br class="lg:hidden" />
								<span class="text-11 font-bold lg:text-base">
									Transmission:&nbsp;
								</span>
								<span class="text-11 lg:text-base">
									{{ trim.licensePlate.transmission }},
								</span>
							</template>
							<br class="lg:hidden" />
							<span class="text-11 font-bold lg:text-base">Doors:&nbsp;</span>
							<span class="text-11 lg:text-base">{{ trim.doors }}</span>
							<template v-if="trim.licensePlate && trim.licensePlate.color">
								<span>,&nbsp;</span>
								<br class="lg:hidden" />
								<span class="text-11 font-bold lg:text-base">Color:&nbsp;</span>
								<span class="text-11 lg:text-base">
									{{ trim.licensePlate.color.name }}
								</span>
							</template>
						</span>

						<div class="ml-2">
							<AppCheckbox
								class="text-success-base mt-1 border-0"
								:checked="
									form.trimModal.trade_pending_id === trim.trade_pending_id &&
									form.trimModal.trim_name === trim.trim_name
								"
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
	import { Trim, useFormStore } from '../../../stores/form'
	import HelpfulTips from '../components/HelpfulTips.vue'
	import AppCheckbox from '../../common/AppCheckbox.vue'
	import { onMounted } from 'vue'
	import { gtagCall } from '../../../services/gtm'
	import CloseSvg from '../components/icons/CloseSvg.vue'
	import { useDialogStore } from '../../../stores/dialog'

	const form = useFormStore()
	const dialogs = useDialogStore()

	// props
	withDefaults(
		defineProps<{
			inHeader?: boolean
		}>(),
		{
			inHeader: false,
		},
	)

	onMounted(() => {
		const event = 'half_lead'
		// check what event fires once for each lead
		if (!form.firedGtagEvents.includes(event)) {
			gtagCall('event', event)
			form.firedGtagEvents.push(event)
		}
	})

	const onSelectTrim = (trim: Trim) => {
		form.trim = trim
	}
	const onSelectTrimModal = (trim: Trim) => {
		form.trimModal = trim
	}
</script>
